/**
 * SubscriptionExpiringModal - Urgent notification for expiring/expired subscriptions
 *
 * Shows when:
 * 1. Trial ends in ≤3 days (TRIAL_ENDING_SOON)
 * 2. Trial expired but in grace period (TRIAL_EXPIRED_GRACE)
 * 3. Subscription expired but in grace period (SUBSCRIPTION_EXPIRED_GRACE)
 *
 * Uses localStorage to only show once per day (not spam on every page load)
 */

import { useState, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import { useNavigate } from 'react-router-dom'

interface SubscriptionExpiringModalProps {
  state: string
  daysRemaining: number | null
  expiresAt: string | null
  onDismiss: () => void
}

// Key for localStorage to track when we last showed the modal
const STORAGE_KEY = 'subscription_expiring_modal_last_shown'

// Only show modal once per 24 hours
const SHOW_INTERVAL_MS = 24 * 60 * 60 * 1000

/**
 * Check if we should show the modal (not shown recently)
 */
function shouldShowModal(): boolean {
  const lastShown = localStorage.getItem(STORAGE_KEY)
  if (!lastShown) return true

  const lastShownTime = parseInt(lastShown, 10)
  return Date.now() - lastShownTime > SHOW_INTERVAL_MS
}

/**
 * Mark modal as shown
 */
function markModalShown(): void {
  localStorage.setItem(STORAGE_KEY, Date.now().toString())
}

/**
 * Clear the modal shown flag (for testing or logout)
 */
export function clearExpiringModalFlag(): void {
  localStorage.removeItem(STORAGE_KEY)
}

export function SubscriptionExpiringModal({
  state,
  daysRemaining,
  expiresAt,
  onDismiss
}: SubscriptionExpiringModalProps) {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  // Check if we should show based on state and timing
  useEffect(() => {
    const relevantStates = [
      'TRIAL_ENDING_SOON',
      'TRIAL_EXPIRED_GRACE',
      'SUBSCRIPTION_EXPIRED_GRACE'
    ]

    if (relevantStates.includes(state) && shouldShowModal()) {
      setIsOpen(true)
      markModalShown()
    }
  }, [state])

  const handleClose = () => {
    setIsOpen(false)
    onDismiss()
  }

  const handleSubscribe = () => {
    handleClose()
    // Navigate to subscription page where users can add payment method
    navigate('/subscription')
  }

  // Determine content based on state
  const getContent = () => {
    const days = daysRemaining || 0

    switch (state) {
      case 'TRIAL_ENDING_SOON':
        return {
          title: 'Your Free Trial is Ending Soon',
          icon: '⏰',
          message: `You have ${days} day${days !== 1 ? 's' : ''} left in your free trial.`,
          subMessage: 'Subscribe now to keep your access and continue enjoying all features.',
          buttonText: 'Subscribe Now',
          urgency: 'warning'
        }

      case 'TRIAL_EXPIRED_GRACE':
        return {
          title: 'Your Free Trial Has Ended',
          icon: '⚠️',
          message: `Your trial has ended! You have ${days} day${days !== 1 ? 's' : ''} to subscribe before losing access.`,
          subMessage: 'Subscribe now to restore full access.',
          buttonText: 'Subscribe Now',
          urgency: 'error'
        }

      case 'SUBSCRIPTION_EXPIRED_GRACE':
        return {
          title: 'Your Subscription Has Expired',
          icon: '🔔',
          message: `Your subscription period has ended! You have ${days} day${days !== 1 ? 's' : ''} to renew before losing access.`,
          subMessage: 'Renew now to continue enjoying all features.',
          buttonText: 'Renew Subscription',
          urgency: 'error'
        }

      default:
        return null
    }
  }

  const content = getContent()

  if (!content || !isOpen) return null

  const urgencyStyles: Record<string, string> = {
    warning: 'bg-yellow-50 border-yellow-500',
    error: 'bg-red-50 border-red-500'
  }

  const buttonStyles: Record<string, string> = {
    warning: 'bg-yellow-500 hover:bg-yellow-600',
    error: 'bg-red-500 hover:bg-red-600'
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="max-w-lg"
    >
      <div className="text-center">
        {/* Icon */}
        <div className="text-5xl mb-4">{content.icon}</div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {content.title}
        </h2>

        {/* Alert Box */}
        <div className={`p-4 rounded-lg border-2 ${urgencyStyles[content.urgency]} mb-6`}>
          <p className="text-lg font-semibold text-gray-800">
            {content.message}
          </p>
        </div>

        {/* Sub Message */}
        <p className="text-gray-600 mb-6">
          {content.subMessage}
        </p>

        {/* Expiration Date */}
        {expiresAt && (
          <p className="text-sm text-gray-500 mb-6">
            Access ends: {new Date(expiresAt).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleSubscribe}
            className={`w-full py-3 px-6 ${buttonStyles[content.urgency]} text-white font-semibold rounded-lg transition-colors`}
          >
            {content.buttonText}
          </button>

          <button
            onClick={handleClose}
            className="w-full py-2 px-4 text-gray-500 hover:text-gray-700 text-sm"
          >
            Remind me later
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default SubscriptionExpiringModal
